import React from 'react';
import { Box, alpha } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';

const SKELETON_OPACITY = 0.28;

const mergeSx = (base: SxProps<Theme>, sx?: SxProps<Theme>): SxProps<Theme> => [
  base,
  ...(Array.isArray(sx) ? sx : [sx]),
];

interface SkeletonBarProps {
  width?: number | string;
  height?: number;
  sx?: SxProps<Theme>;
}

export const SkeletonBar = ({ width = '100%', height = 6, sx }: SkeletonBarProps) => (
  <Box
    sx={mergeSx(
      {
        width,
        height,
        flexShrink: 0,
        borderRadius: `${height / 2}px`,
        backgroundColor: 'text.primary',
        opacity: SKELETON_OPACITY,
      },
      sx,
    )}
  />
);

interface SkeletonBlockProps {
  width?: number | string;
  height?: number | string;
  radius?: number;
  blur?: number;
  sx?: SxProps<Theme>;
}

export const SkeletonBlock = ({ width = '100%', height = 24, radius = 4, blur, sx }: SkeletonBlockProps) => (
  <Box
    sx={mergeSx(
      {
        width,
        height,
        flexShrink: 0,
        borderRadius: `${radius}px`,
        backgroundColor: 'text.primary',
        opacity: SKELETON_OPACITY,
        ...(blur ? { filter: `blur(${blur}px)` } : null),
      },
      sx,
    )}
  />
);

interface HighlightFrameProps {
  children?: React.ReactNode;
  sx?: SxProps<Theme>;
}

export const HighlightFrame = ({ children, sx }: HighlightFrameProps) => (
  <Box
    sx={mergeSx(
      theme => ({
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        padding: '4px 8px',
        borderRadius: '7px',
        border: `2px solid ${theme.palette.primary.main}`,
        boxShadow: `0 0 0 5px ${alpha(theme.palette.primary.main, 0.16)}`,
        backgroundColor: theme.palette.background.paper,
      }),
      sx,
    )}
  >
    {children}
  </Box>
);

interface CoachmarkCanvasProps {
  children?: React.ReactNode;
  sx?: SxProps<Theme>;
}

export const CoachmarkCanvas = ({ children, sx }: CoachmarkCanvasProps) => (
  <Box
    aria-hidden="true"
    sx={mergeSx(
      theme => ({
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        padding: 1.25,
        borderRadius: '8px',
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        overflow: 'hidden',
      }),
      sx,
    )}
  >
    {children}
  </Box>
);
